import { Component, ViewEncapsulation, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';

import { Client } from 'src/providers/client/client';
import { Menu } from 'src/providers/menu/menu';

import { Util } from 'src/shared/util/util';
import { Loading } from 'src/shared/components/loading/loading';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { AccountManagerService } from 'src/shared/services/account-manager.service';
import { AccountManagerModalComponent } from 'src/shared/components/account-manager-modal/account-manager-modal.component';
import { DemoBlockModalComponent } from 'src/shared/components/demo-block-modal/demo-block-modal.component';

import { DEVELOPER_API_REFERENCE_URL } from 'src/shared/constants/developer-support';

@Component({
    templateUrl: 'api-keys.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class ApiKeysPage implements OnInit {

  apiKeys: {
    id: string,
    token: string,
  }[];
  deletingKey = false;
  deletedKeyId: number;
  errorMessage: string;
  form: UntypedFormGroup;
  title = 'API Keys';
  loaded: boolean;
  // Distinguishes "the list failed to load" from "this account genuinely has no keys". An empty
  // array alone would tell a customer holding a live key that they have none, and take their token
  // and its Delete Key link off the page with it.
  loadFailed: boolean;
  isRightSideContent: boolean;


  boundRequestApiAccess = this.requestApiAccess.bind(this);
  boundStopDeletingApiKey = this.stopDeletingApiKey.bind(this);
  boundConfirmDeleteApiKey = this.confirmDeleteApiKey.bind(this);

constructor(
    private accountManagerService: AccountManagerService,
    private client: Client,
    private fb: UntypedFormBuilder,
    private modalController: ModalController,
    private util: Util,
    public menu: Menu,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    private windowService: WindowService
  ) {
    this.form = this.fb.group({
      checkbox: [false, Validators.requiredTrue]
    });
  }

  @Loading('', true)
  async ngOnInit() {
    this.loaded = false;
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    if (this.isRightSideContent) {
      this.rightSidePanelService.setDialogPageTitle('API Keys');
    }
    this.errorMessage = '';
    await this.loadApiKeys();
    // Left unawaited on purpose — ngOnInit's promise is what @Loading holds its bar up for, and the
    // bar should clear as the prompt opens, not once the customer dismisses it.
    if (this.loadFailed) {
      this.requestApiAccess();
    }
  }

  // The endpoint currently rejects browser traffic ("A valid User-Agent header is required") and is
  // slow enough to time out, so it fails for effectively every account in production. Keys are
  // handed out manually anyway, so a failure raises the same account-manager prompt the CTA does
  // rather than an error card whose only affordance is retrying into the same 400.
  private async loadApiKeys(): Promise<void> {
    try {
      this.apiKeys = await this.client.getApiKeys();
      this.loadFailed = false;
    } catch {
      this.apiKeys = [];
      this.loadFailed = true;
    }
    this.loaded = true;
  }

  // Keys are handed out manually after a conversation, so creating one in-app is gated behind
  // the same prompt the demo/new-rental gates use, with API-specific copy.
  //
  // Returns synchronously on purpose: tidy-button raises a global loading overlay for as long as
  // its [action] promise is pending, and that overlay both disables and swallows clicks on every
  // other tidy-button. Releasing it before the prompt opens keeps the prompt's CTA live.
  requestApiAccess(): void {
    void this.openApiAccessPrompt();
  }

  private async openApiAccessPrompt(): Promise<void> {
    const modal = await this.modalController.create({
      component: DemoBlockModalComponent,
      componentProps: {
        prompt: {
          title: 'Contact Your Account Manager',
          body: 'Please contact your account manager to get set up with API keys.',
          buttonLabel: 'Contact Account Manager'
        }
      },
      cssClass: 'demo-block-modal-wrapper',
      backdropDismiss: true
    });
    modal.onDidDismiss().then(({ data }) => {
      if (data?.getStarted) {
        this.contactAccountManager();
      }
    });
    await modal.present();
  }

  // The account manager modal only renders in the desktop right side panel — it has no mobile
  // route — so mobile goes straight to the manager's inbox, the modal's own primary action.
  private async contactAccountManager() {
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.setDialogPageTitle('Your Account Manager');
      this.rightSidePanelService.openRightSidePanel(AccountManagerModalComponent, { smallDisplay: true });
      return;
    }
    const manager = await this.accountManagerService.getDisplay();
    window.location.href = `mailto:${manager.email}`;
  }

  async deleteApiKey(id) {
    this.title = 'Delete API Key?';
    this.rightSidePanelService.setDialogPageTitle(this.title);
    this.deletingKey = true;
    this.deletedKeyId = id;
  }

  async confirmDeleteApiKey() {
    this.errorMessage = '';
    if (this.form.invalid) {
      return this.errorMessage = 'Please confirm that you want to delete the key.'
    }
    try {
      await this.client.deleteApiKey(this.deletedKeyId);
      this.apiKeys = await this.client.getApiKeys();
      this.stopDeletingApiKey();
    } catch(err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  stopDeletingApiKey() {
    this.title = 'API Keys';
    this.rightSidePanelService.setDialogPageTitle(this.title);
    this.deletingKey = false;
  }

  goToApiDocumentation() {
    this.util.openUrl(DEVELOPER_API_REFERENCE_URL);
  }
}
